/* @flow */

import { FloatingDelayGroup } from '@floating-ui/react';
import * as React from 'react';
import { useMemo } from 'react';

import type { ReadOnlyKeyValuePair } from '../../helpers/jsHelpers/types';
import type { NETGEM_API_V8_METADATA_PROGRAM, NETGEM_API_V8_METADATA_SERIES } from '../../libs/netgemLibrary/v8/types/MetadataProgram';

import { TOOLTIP } from '../../helpers/ui/constants';
import {
  NETGEM_API_V8_CREDIT_ROLE_ACTOR,
  NETGEM_API_V8_CREDIT_ROLE_DIRECTOR,
  NETGEM_API_V8_CREDIT_ROLE_GUEST,
  NETGEM_API_V8_CREDIT_ROLE_PRESENTER,
  type NETGEM_API_V8_CREDIT_ROLE_TYPE,
} from '../../libs/netgemLibrary/v8/types/Credit';
import './Credits.css';
import CreditsByRole from './CreditsByRole';

const MAX_PEOPLE_BY_ROLE: ReadOnlyKeyValuePair<number> = Object.freeze({
  [NETGEM_API_V8_CREDIT_ROLE_ACTOR]: 10,
  [NETGEM_API_V8_CREDIT_ROLE_DIRECTOR]: 3,
  [NETGEM_API_V8_CREDIT_ROLE_GUEST]: 3,
  [NETGEM_API_V8_CREDIT_ROLE_PRESENTER]: 3,
});

type CreditsPropType = {
  +metadata: NETGEM_API_V8_METADATA_PROGRAM | NETGEM_API_V8_METADATA_SERIES | null,
};

const keyByRole: ReadOnlyKeyValuePair<string> = Object.freeze({
  [NETGEM_API_V8_CREDIT_ROLE_ACTOR]: 'credits.with',
  [NETGEM_API_V8_CREDIT_ROLE_DIRECTOR]: 'credits.by',
  [NETGEM_API_V8_CREDIT_ROLE_GUEST]: 'credits.with',
  [NETGEM_API_V8_CREDIT_ROLE_PRESENTER]: 'credits.presented',
});

const orderedRoles: Array<NETGEM_API_V8_CREDIT_ROLE_TYPE> = Object.freeze([
  NETGEM_API_V8_CREDIT_ROLE_DIRECTOR,
  NETGEM_API_V8_CREDIT_ROLE_PRESENTER,
  NETGEM_API_V8_CREDIT_ROLE_GUEST,
  NETGEM_API_V8_CREDIT_ROLE_ACTOR,
]);

const Credits = ({ metadata }: CreditsPropType): React.Node => {
  const listByRole = useMemo(() => {
    const obj: { [NETGEM_API_V8_CREDIT_ROLE_TYPE]: Array<string> } = {};
    orderedRoles.forEach((role) => (obj[role] = []));

    if (!metadata) {
      return obj;
    }

    const { credits } = metadata;
    if (!credits) {
      // All lists are empty
      return obj;
    }

    for (let i: number = 0; i < credits.length; i++) {
      const { name, role } = credits[i];

      if (
        (role === NETGEM_API_V8_CREDIT_ROLE_ACTOR || role === NETGEM_API_V8_CREDIT_ROLE_DIRECTOR || role === NETGEM_API_V8_CREDIT_ROLE_GUEST || role === NETGEM_API_V8_CREDIT_ROLE_PRESENTER) &&
        obj[role].length < MAX_PEOPLE_BY_ROLE[role]
      ) {
        obj[role].push(name);
      }
    }

    return obj;
  }, [metadata]);

  if (!metadata) {
    return null;
  }

  return (
    <FloatingDelayGroup delay={{ close: TOOLTIP.DefaultCloseDelay, open: TOOLTIP.DefaultOpenDelay }} timeoutMs={TOOLTIP.GroupTimeout}>
      <div className='credits'>
        {orderedRoles.map((role) => (
          <CreditsByRole key={role} labelKey={keyByRole[role]} names={listByRole[role]} />
        ))}
      </div>
    </FloatingDelayGroup>
  );
};

export default Credits;
